
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import Logo from '@/components/common/Logo';
import { Loader2 } from 'lucide-react';
import UpdatePasswordForm from '@/components/auth/UpdatePasswordForm';
import PasswordResetSuccess from '@/components/auth/PasswordResetSuccess';
import ErrorMessage from '@/components/common/ErrorMessage';
import { logger } from '@/lib/logger';
import { APP_NAME } from '@/config/environment';
import { Button } from '@/components/ui/button';

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isProcessing, setIsProcessing] = useState(true);
  const [needsNewResetLink, setNeedsNewResetLink] = useState(false);

  // Get the email from the URL if available
  const emailFromUrl = searchParams.get('email');
  // Get the token from hash or query params
  const tokenFromHash = window.location.hash.replace(/^#/, '');
  const tokenFromQuery = searchParams.get('token');
  const token = tokenFromHash || tokenFromQuery;

  useEffect(() => {
    // Log information about the token and URL parameters 
    logger.info({
      message: "Reset password page accessed",
      context: { 
        hasEmail: !!emailFromUrl,
        hasToken: !!token,
        tokenSource: tokenFromHash ? 'hash' : (tokenFromQuery ? 'query' : 'none'),
        fullUrl: window.location.href
      }
    });

    // Check if this is a valid reset password link
    const handlePasswordReset = async () => {
      try {
        setIsProcessing(true);
        
        // Check for a valid session first
        const { data: sessionData } = await supabase.auth.getSession();
        
        // If we've got a valid recovery session, we're good
        if (sessionData?.session) {
          logger.info({
            message: "Valid password reset session detected",
            context: { userId: sessionData.session.user.id }
          });
          setIsProcessing(false);
          return;
        }
        
        // Check if we have a token
        if (token) {
          logger.info({
            message: "Reset token found, attempting to verify",
            context: { tokenLength: token.length }
          });
          
          // Try to exchange the token for a session
          const { error: tokenError } = await supabase.auth.verifyOtp({
            token_hash: token,
            type: 'recovery'
          });
          
          if (tokenError) {
            logger.error({
              message: "Invalid or expired reset token",
              error: new Error(tokenError.message)
            });
            
            setError("This password reset link is invalid or has expired. Please request a new password reset link.");
            setNeedsNewResetLink(true);
          } else {
            // Token is valid, let the user reset their password
            setIsProcessing(false);
            return;
          }
        } else {
          // No token found
          if (!emailFromUrl) {
            logger.warn({
              message: "Reset password page accessed without email parameter or token"
            });
            setError("Invalid password reset link. Please request a new password reset link from the login page.");
          } else {
            logger.info({
              message: "Email found in URL but no token, prompting user to request a new reset link",
              context: { email: emailFromUrl }
            });
            setError(`To reset your password for ${emailFromUrl}, please request a new reset link.`);
          }
          setNeedsNewResetLink(true);
        }
      } catch (error: any) {
        logger.error({
          message: "Error processing password reset",
          error: new Error(error.message)
        });
        
        setError(error.message || "An unexpected error occurred. Please try again.");
        setNeedsNewResetLink(true);
        
        toast({
          title: "Error",
          description: error.message || "An unexpected error occurred. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsProcessing(false);
      }
    };

    handlePasswordReset();
  }, [navigate, toast, emailFromUrl, token, tokenFromHash, tokenFromQuery]);

  const handleResetSuccess = () => {
    setSuccess(true);
    logger.info({
      message: "Password reset completed successfully"
    });
    
    // Redirect to login after a short delay
    setTimeout(() => {
      navigate('/login');
    }, 3000);
  };

  const handleRequestNewLink = () => {
    // Redirect to the reset password request page
    // If we have the email, we can pre-populate it
    if (emailFromUrl) {
      // Create a custom event to set the email in the reset form
      const event = new CustomEvent('set-reset-email', { detail: emailFromUrl });
      window.dispatchEvent(event);
    }
    
    navigate('/forgot-password');
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-b from-background to-muted/50 p-4">
      <div className="w-full max-w-md space-y-8">
        <div className="flex flex-col items-center justify-center mb-8">
          <Logo size="xl" />
          <h1 className="text-3xl font-bold mt-4">{APP_NAME}</h1>
          <p className="text-muted-foreground mt-2">Reset Your Password</p>
        </div>

        <Card className="p-6">
          {isProcessing ? (
            <div className="flex justify-center items-center p-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : success ? (
            <PasswordResetSuccess />
          ) : (
            <>
              <ErrorMessage message={error} />
              {!error && (
                <UpdatePasswordForm onSuccess={handleResetSuccess} />
              )}
              {needsNewResetLink && (
                <div className="mt-6 flex flex-col items-center">
                  <p className="text-center text-sm text-muted-foreground mb-4">
                    Password reset links expire after 24 hours. You need to request a new link.
                  </p>
                  <Button 
                    onClick={handleRequestNewLink}
                    className="w-full"
                  >
                    Request New Reset Link
                  </Button>
                </div>
              )}
              <div className="text-center mt-4">
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="text-primary text-sm hover:underline bg-transparent border-none p-0 cursor-pointer"
                >
                  Back to Sign In
                </button>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
